import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { History, Loader2, Monitor } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface SignInLog {
  id: string;
  user_id: string;
  email: string | null;
  display_name: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

const parseUserAgent = (ua: string | null) => {
  if (!ua) return { browser: "Unknown", os: "Unknown" };
  
  let browser = "Unknown";
  let os = "Unknown";
  
  // Detect browser
  if (ua.includes("Firefox")) browser = "Firefox";
  else if (ua.includes("Edg")) browser = "Edge";
  else if (ua.includes("Chrome")) browser = "Chrome";
  else if (ua.includes("Safari")) browser = "Safari";
  
  // Detect OS
  if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Mac OS")) os = "macOS";
  else if (ua.includes("Linux")) os = "Linux";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";
  
  return { browser, os };
};

const SignInLogsAdmin = () => {
  const { data: logs, isLoading } = useQuery({
    queryKey: ["sign-in-logs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sign_in_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);

      if (error) throw error;
      return data as SignInLog[];
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <History className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-display font-bold">Sign-In Logs</h1>
          <p className="text-muted-foreground text-sm">
            Recent authentication events for admin users
          </p>
        </div>
      </div>

      {logs && logs.length > 0 ? (
        <div className="bg-background rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Date & Time</TableHead>
                <TableHead>Device</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => {
                const { browser, os } = parseUserAgent(log.user_agent);
                const initial = log.display_name?.charAt(0).toUpperCase() || 
                               log.email?.charAt(0).toUpperCase() || "?";
                
                return (
                  <TableRow key={log.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-muted text-muted-foreground text-xs">
                            {initial}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">
                            {log.display_name || "Unknown"}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {log.email || "No email"}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {format(new Date(log.created_at), "MMM d, yyyy")}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {format(new Date(log.created_at), "h:mm a")}
                      </div>
                    </TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-2">
                              <Monitor className="h-4 w-4 text-muted-foreground" />
                              <div className="flex gap-1">
                                <Badge variant="secondary" className="text-xs">
                                  {browser}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {os}
                                </Badge>
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-sm">
                            <p className="text-xs font-mono break-all">
                              {log.user_agent || "No user agent"}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="bg-background rounded-lg border p-12 text-center">
          <History className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No sign-in logs yet</h3>
          <p className="text-muted-foreground">
            Sign-in events will appear here as users log in.
          </p>
        </div>
      )}
    </div>
  );
};

export default SignInLogsAdmin;
